import { useEffect, useState } from "react";
import { Download, X, Share } from "lucide-react";

const DISMISS_KEY = "kadana_pwa_install_dismissed_v1";

// Lightweight "Add to Home Screen" prompt.
// - Chrome/Android/Edge: captures the beforeinstallprompt event and shows a native install button.
// - iOS Safari (no beforeinstallprompt): shows a short Share -> Add to Home Screen hint.
// Dismissal is remembered; never shown when the app is already running standalone.
export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
    const dismissed = localStorage.getItem(DISMISS_KEY) === "1";
    if (standalone || dismissed) return;

    const ua = window.navigator.userAgent || "";
    const ios = /iphone|ipad|ipod/i.test(ua) && !/crios|fxios/i.test(ua); // iOS Safari only
    if (ios) { setIsIOS(true); setShow(true); return; }

    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); setShow(true); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    const onInstalled = () => { setShow(false); localStorage.setItem(DISMISS_KEY, "1"); };
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => { setShow(false); try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ } };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch { /* ignore */ }
    setDeferred(null);
    dismiss();
  };

  if (!show) return null;

  return (
    <div data-testid="pwa-install-prompt" className="k-fadein" role="dialog" aria-label="Add Kadana to your home screen"
      style={{ position: "fixed", left: 12, right: 12, bottom: 12, zIndex: 1300, margin: "0 auto", maxWidth: 460,
        background: "var(--container)", border: "1px solid var(--border-strong)", borderRadius: 14,
        padding: 14, display: "flex", alignItems: "center", gap: 12, boxShadow: "0 10px 30px rgba(0,0,0,.45)" }}>
      <img src="/icons/kadana-192.png" alt="" width={40} height={40} style={{ borderRadius: 9, flex: "none" }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="k-heading" style={{ fontSize: 14, fontWeight: 700 }}>Add Kadana to your home screen</div>
        {isIOS ? (
          <div className="k-muted" style={{ fontSize: 12, marginTop: 2, display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
            Tap <Share size={13} style={{ verticalAlign: "middle" }} /> then “Add to Home Screen”.
          </div>
        ) : (
          <div className="k-muted" style={{ fontSize: 12, marginTop: 2 }}>Launch it fast, full-screen — like an app.</div>
        )}
      </div>
      {!isIOS && (
        <button className="k-btn k-btn-primary" data-testid="pwa-install-btn" onClick={install}
          style={{ padding: "9px 14px", minHeight: 0, flex: "none" }}>
          <Download size={14} /> Install
        </button>
      )}
      <button className="k-btn k-btn-ghost" data-testid="pwa-install-dismiss" aria-label="Dismiss" onClick={dismiss}
        style={{ padding: "8px 10px", minHeight: 0, flex: "none" }}><X size={15} /></button>
    </div>
  );
}
